import { useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileUp, ExternalLink, Trash2, FileText } from "lucide-react";

export function FeedbackPdfUpload({ kind, id, url, onChange, hint }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/feedback-pdf/${kind}/${id}`, fd);
      onChange(data.feedback_pdf_url);
      toast.success("Feedback PDF attached. Students see it with their feedback.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async () => {
    try { await api.delete(`/feedback-pdf/${kind}/${id}`); onChange(null); toast.success("Feedback PDF removed."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-dashed border-[#C4B5FD] bg-[#FAF9FE] p-4" data-testid="feedback-pdf-upload">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#F3F0FF] text-[#4B2BB8]"><FileText className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-[#1E1B2E]">Feedback PDF <span className="font-normal text-[#948EAB]">(optional)</span></div>
          <p className="text-xs text-[#948EAB]">{hint || "Upload a marked-up PDF instead of, or on top of, the feedback above."}</p>
          {url && <a href={`${BACKEND_URL}${url}`} target="_blank" rel="noreferrer" data-testid="feedback-pdf-link" className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#4B2BB8] hover:underline"><ExternalLink className="h-3.5 w-3.5" /> View attached PDF</a>}
        </div>
      </div>
      <input ref={ref} type="file" accept=".pdf,application/pdf" className="hidden" data-testid="feedback-pdf-input" onChange={(e) => upload(e.target.files?.[0])} />
      <div className="mt-3 flex gap-2">
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="feedback-pdf-btn" className="rounded-lg text-xs font-bold">
          <FileUp className="mr-1 h-3.5 w-3.5" /> {busy ? "Uploading…" : url ? "Replace PDF" : "Upload PDF"}
        </Button>
        {url && <Button type="button" size="sm" variant="ghost" onClick={remove} data-testid="feedback-pdf-remove" className="rounded-lg text-xs text-rose-600"><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button>}
      </div>
    </div>
  );
}

export function FeedbackPdfView({ url }) {
  if (!url) return null;
  return (
    <div className="mt-5 overflow-hidden rounded-xl border border-[#EEEAF8]" data-testid="feedback-pdf-view">
      <div className="flex items-center justify-between bg-[#F3F0FF] px-4 py-2.5">
        <span className="text-sm font-bold text-[#4B2BB8]">Your tutor's feedback (PDF)</span>
        <a href={`${BACKEND_URL}${url}`} target="_blank" rel="noreferrer" data-testid="feedback-pdf-open" className="inline-flex items-center gap-1 text-xs font-bold text-[#4B2BB8] hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Open in new tab</a>
      </div>
      <iframe title="feedback" src={`${BACKEND_URL}${url}#view=FitH`} className="h-[70vh] w-full bg-white" />
    </div>
  );
}
